import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Col, Input, Modal, Row, Switch, Tooltip } from 'antd';

import { EChargeType } from 'interfaces/IEditProduct';
import type IProduct from 'interfaces/IProduct';
import { FormContent } from 'scenes/Producer/Product/EditProduct/components/FormContent';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import Validations from './validations';

interface IProps {
  product: IProduct;
  handleChange?: any;
  enabledValidation?: boolean;
}

function SalesRecovery(props: IProps) {
  const { product, handleChange, enabledValidation } = props;
  const { user } = useUserStore(({ user }) => ({ user }));

  const [validationErrors, setValidationErrors] = useState<any>([]);
  const [showAlertUncheck, setShowAlertUncheck] = useState(false);

  const prevProductRef = useRef(product);
  const prevEnabledValidationRef = useRef(enabledValidation);

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const handleToggleAlertVisibility = useCallback(() => {
    setShowAlertUncheck(prev => !prev);
  }, []);

  const handleUncheckConfirmed = useCallback(
    (currentProduct: IProduct) => {
      currentProduct.recovery_email = '';

      if (handleChange) {
        handleChange('recovery_email', '');
        handleChange('sales_recovery', false);
      }

      handleToggleAlertVisibility();
    },
    [handleChange, handleToggleAlertVisibility]
  );

  useEffect(() => {
    if (!product.recovery_email) {
      if (handleChange) handleChange('recovery_email', product.recovery_email || user.business_name || user.full_name);
    }
  }, [user.business_name, user.full_name, handleChange, product]);

  useEffect(() => {
    const prevProduct = prevProductRef.current;
    const prevEnabledValidation = prevEnabledValidationRef.current;
    const hasChangedProduct = product !== prevProduct;

    if (hasChangedProduct) {
      const isOpenValue = product.charge_type === EChargeType.OTHER;

      if (isOpenValue && product.sales_recovery) {
        if (handleChange) handleChange('sales_recovery', false);
      }
    }

    const hasEnabledValidation = !prevEnabledValidation && enabledValidation;
    const hasChangedProductWithValidation = enabledValidation && hasChangedProduct;
    if (hasEnabledValidation || hasChangedProductWithValidation) setValidationErrors({ ...run(product, Validations) });
    prevProductRef.current = product;
    prevEnabledValidationRef.current = enabledValidation;
  }, [product, enabledValidation, handleChange]);

  const isOpenValue = product.charge_type === EChargeType.OTHER;

  return (
    <FormContent title='Recuperação de vendas'>
      <Row gutter={[16, 16]} align='middle'>
        <Col span={24}>
          <Row gutter={[16, 16]} align='middle'>
            <Col span={24}>
              <Tooltip
                title={isOpenValue ? '*Não é possível ativar a recuperação com Valor Aberto' : ''}
                placement='left'
              >
                <label className='flex items-center gap-2' id='sales_recovery'>
                  <Switch
                    disabled={isOpenValue}
                    checked={product.sales_recovery}
                    onChange={checked => {
                      if (checked) {
                        if (handleChange) handleChange('sales_recovery', checked);
                        return;
                      }

                      setShowAlertUncheck(true);
                    }}
                  />
                  Recuperar vendas perdidas
                </label>
              </Tooltip>
              <span className='mt-1! mb-0! block text-xs text-gray-500'>
                A Eduzz tentará recuperar vendas não concretizadas.{' '}
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807625289-Como-funciona-a-recupera%C3%A7%C3%A3o-de-vendas-perdidas-RVP-'
                >
                  Saiba Mais
                </a>
              </span>
            </Col>

            {product.sales_recovery && (
              <Col span={24}>
                <Tooltip title='O nome definido neste campo aparecerá como remetente nos e-mails enviados de Recuperação de Vendas.'>
                  <div className='relative z-10 my-4'>
                    <label className='mb-1 block text-sm'>Nome do remetente de e-mail para recuperação de vendas</label>
                    <Input
                      id='recovery_email'
                      status={isValid('recovery_email') ? 'error' : undefined}
                      value={product.recovery_email}
                      onChange={(e: any) => {
                        if (handleChange) handleChange('recovery_email', e.target.value);
                      }}
                      disabled={!product.sales_recovery}
                    />
                    <span
                      className={`mt-1 block text-xs ${isValid('recovery_email') ? 'text-red-500' : 'text-gray-500'}`}
                    >
                      {isValid('recovery_email') ? errorFor('recovery_email') : 'Ex: Fulano da Silva - Eduzz'}
                    </span>
                  </div>
                </Tooltip>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      <Modal
        open={showAlertUncheck}
        onCancel={handleToggleAlertVisibility}
        title='Tem certeza que deseja desativar esta funcionalidade?'
        footer={[
          <Button key='deactivate' onClick={() => handleUncheckConfirmed(product)}>
            Desativar mesmo assim
          </Button>,
          <Button key='keep' type='primary' onClick={handleToggleAlertVisibility}>
            Manter ativado
          </Button>
        ]}
      >
        <p>
          Mantenha a RVP ativa para recuperar até 10% das suas vendas e aumentar suas conversões com clientes que
          abandonam o carrinho de forma automática.
        </p>
      </Modal>
    </FormContent>
  );
}

export default SalesRecovery;
